import { ReactNode, useMemo, useEffect, useState } from 'react';
import { useProjectContext } from '~/hooks';
import { Label, Container } from 'semantic-ui-react';
import MainContainer from './MainContainer';
import DataroomCounts from './DataroomCounts';
import PageHeader from '../PageHeader';
import { PageHeaderProvider } from '../PageHeader/PageHeaderProvider';
import { restorePortfolioNavScroll } from '~/utils/portfolioNavScroll';
import { ContractType, DealStates, PricingType, ProjectInterconnectionTypeCodes } from '~/constants';
import { useLocation } from 'react-router-dom';
import ProjectSubmissionButtons from './components/ProjectSubmissionButtons';
import PageLoader from '../PageLoader';
import { useRequiredResponseCountsQuery } from '~/requests/projects/useRequiredResponseCounts';
import { usePermissions } from '~/requests/permissions/usePermissions';
import { useProjectTeamQuery } from '~/requests/team';
import { getDataroomCounts } from '~/utils/calculators/dataroomCounts';
import { useDataroomQuery } from '~/requests/dataroom/useDataroomQuery';
import AdditionalActions from '../AdditionalActions';
import ArchiveAction from '../AdditionalActions/ArchiveAction';
import { DeploymentTypes } from '~/constants/deployment-type';
import ProjectCustomerProposal from '../ProjectCustomerProposal';
import AdminAutopricingLinkAction from '../AdditionalActions/AdminAutopricingLinkAction';
import OwnershipPicker from '~/components/OwnershipPicker';
import Timestamp from '~/components/Timestamp';
import { getMarketplaceListingById } from '~/api/marketplace';
import './styles.scss';

export default function ProjectPage({ children }: { children: ReactNode }) {
  const { project, fetchStatus, projectState } = useProjectContext();
  const { status: permissionStatus, permissions } = usePermissions();
  const { status: teamStatus } = useProjectTeamQuery();
  const { status: dataroomStatus, data: dataroom } = useDataroomQuery({
    onError(err) {
      console.error(err);
      // Do nothing. This can happen if user does not have dataroom access
    }
  });
  const dealState = projectState?.dealState;
  const [listing, setListing] = useState<any>(null);

  const showCustomerProposalOption = permissions?.hasProjectDeveloperAccess
    && project?.deploymentType === DeploymentTypes.CI
    && project?.contractType === ContractType.PPA
    && project?.pricingType === PricingType.OFFTAKE
    && project?.interconnectionType === ProjectInterconnectionTypeCodes.BEHIND_METER;

  const { data: requiredResponseCountsData, isFetching } = useRequiredResponseCountsQuery();
  const requiredResponseCounts = requiredResponseCountsData?.reduce((acc: number, curr: any) => acc + curr.count, 0) ?? 0;

  const { pathname } = useLocation();

  const isInvestor = permissions?.hasProjectInvestorAccess && !permissions.isAdmin;
  const dataroomCounts = useMemo(() => {
    return dataroom ? getDataroomCounts(dataroom.documents) : null;
  }, [dataroom]);

  const showDataroomTab = permissions?.canReadDataroom && !(isInvestor && dealState < DealStates.IN_DILIGENCE && !dataroomCounts?.uploaded);

  const [showBiddingTab, setShowBiddingTab] = useState<boolean>(false);

  useEffect(() => {
    fetchListing();
    if (permissions?.isAdmin) {
      setShowBiddingTab(true);
      return;
    }
    if (
      projectState?.dealState >= DealStates.IN_DILIGENCE ||
      projectState?.dealState === DealStates.DECLINED ||
      projectState?.dealState < DealStates.IN_REVIEW
    ) {
      setShowBiddingTab(false);
    } else {
      setShowBiddingTab(true);
    }
  }, [projectState, permissions?.isAdmin]);

  useEffect(() => {
    if (fetchStatus !== 'loading' && project?.id) {
      restorePortfolioNavScroll();
    }
  }, [fetchStatus, project?.id]);

  const fetchListing = async () => {
    try {
      const data = await getMarketplaceListingById({ projectId: project.id });
      setListing(data.isVisible ? data : null);
    } catch (err) {
      setListing(null);
    }
  };

  if (fetchStatus === 'loading' || permissionStatus === 'loading' || teamStatus === 'loading' || dataroomStatus === 'loading') {
    return <div style={{ width: '100%', position: 'relative', height: '100vh' }}><PageLoader /></div>;
  } else if (project?.id) {
    return (
      <PageHeaderProvider>
      <Container>
        <div className="metadata-header">
          {permissions.isAdmin && <OwnershipPicker />}
          <div style={{ marginLeft: 'auto' }}>
            <Timestamp project={project} />
          </div>
        </div>
        <PageHeader
          project={project}
          navTabs={[{
            path: `/project/${project.id}/review`,
            content: 'Summary',
          }, showBiddingTab && {
            path: `/project/${project.id}/bidding`,
            content: 'Bidding',
          },
          !isInvestor && {
            path: `/project/${project.id}/worksheet`,
            content: 'Worksheet',
          },
          permissions.isAdmin && {
            path: `/project/${project.id}/ingestion`,
            content: 'Import',
          },
          showDataroomTab && {
            path: `/project/${project.id}/dataroom`,
            content: (
              <div style={{ display: 'flex', alignItems: 'center' }}>
                <div>Data Room</div>
                <DataroomCounts />
              </div>
            ),
          }, {
            path: `/project/${project.id}/messages`,
            content: (
              <div style={{ display: 'flex', alignItems: 'center' }}>
                <div>Messages</div>
                {isFetching ? null : (
                  pathname !== `/project/${project.id}/messages` && requiredResponseCounts > 0 && (
                    <Label circular color='red' style={{ marginLeft: 'var(--x-small)' }}>
                      {requiredResponseCounts}
                    </Label>
                  )
                )}
              </div>
            ),
          }, permissions.canManageProjectTeam && {
            path: `/project/${project.id}/team`,
            content: 'Team'
          }, (permissions.isAdmin || (permissions.isDeveloper && listing)) && {
            path: `/project/${project.id}/listing`,
            content: 'Listing'
          }
          ]}
          secondaryNavContent={
            <div className="secondary-actions-container">
              {(
                !project?.isArchived && 
                !permissions.isInvestor &&
                projectState?.dealState !== undefined && 
                projectState.dealState < DealStates.IN_REVIEW
                ) ? <ProjectSubmissionButtons /> : null}

              <AdditionalActions>
                {permissions.hasArchiveActions && <ArchiveAction />}
                {showCustomerProposalOption && <ProjectCustomerProposal />}
                {permissions.isAdmin && <AdminAutopricingLinkAction />}
              </AdditionalActions>
            </div>
          }
        />

        <MainContainer>{children}</MainContainer>
      </Container>
      </PageHeaderProvider>
    );
  } else {
    return null;
  }
}
